import { useQuery } from "@tanstack/react-query";
import { axiosClient } from "auth-sdk";
import type { WayflyerSchemas } from "api-types";
import { addError } from "@wayflyer/blackbox-fe";
import { useCustomerAppConfig } from "undercarriage";

const isProduction = import.meta.env.PROD && !window.origin.includes("dev");
const shouldShowDefaultLauncher =
  window.location.pathname.startsWith("/funding/how-to-apply") ||
  window.location.pathname.startsWith("/funding/wayflyer-hosted-capital") ||
  window.location.pathname.startsWith("/benchmarks") ||
  window.location.pathname.startsWith("/verify-identity");

export const useIntercomLoaded = () => {
  const { data: appConfig } = useCustomerAppConfig();
  const customerUuid = appConfig?.customerUuid;
  const customerName = appConfig?.customerName;

  const { data: isIntercomLoaded } = useQuery({
    queryKey: ["intercom-chat-widget", customerUuid],
    queryFn: async () => {
      let intercomData;

      try {
        const { data } = await axiosClient.get<WayflyerSchemas.IntercomConfigSchema>("/api/wayflyer/intercom/config");
        intercomData = data;
      } catch {
        intercomData = {
          app_id: "vrn7wq9n",
          api_base: "https://api-iam.eu.intercom.io",
        };
      }

      try {
        const Intercom = await import("@intercom/messenger-js-sdk");
        Intercom.default({
          app_id: intercomData.app_id,
          api_base: intercomData.api_base,
          region: "eu",
          intercom_user_jwt: intercomData.jwt_token,
          company_id: customerUuid ?? undefined,
          company: {
            id: customerUuid ?? undefined,
            company_id: customerUuid ?? undefined,
            name: customerName ?? undefined,
          },
          hide_default_launcher: !shouldShowDefaultLauncher && isProduction,
        });
        return true;
      } catch (error) {
        addError("Failed to initialize Intercom", error);
        return false;
      }
    },
    enabled: isProduction,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return isIntercomLoaded;
};
